import { useState } from "react";
import { Form, Formik, type FormikHelpers } from "formik";
import { array, number, object } from "yup";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Callout from "../UI/Callout";
import SearchInput from "../UI/SearchInput";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import { useToast } from "../UI/Toast/useToast";
import Text from "../UI/Text";
import type { Property } from "../../models/property";
import styles from "./DuplicateRooms.module.css";

interface EdlItemOption {
  id: number;
  nom: string;
}

type PropertyWithEdlItems = Property & {
  edl_items?: EdlItemOption[];
};

interface DuplicateRoomsFormValues {
  pieces: string[];
}

interface DuplicateRoomsProps {
  propertyId: number;
  onSubmit: (pieceIds: number[]) => Promise<void> | void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DuplicateRooms({
  propertyId,
  onSubmit,
  open,
  onOpenChange,
}: DuplicateRoomsProps) {
  const { properties }: { properties: PropertyWithEdlItems[] } = useProperties({
    params: { include: ["edl_items"] },
  });
  const [filter, setFilter] = useState("");
  const toast = useToast();

  async function submitHandler(
    values: DuplicateRoomsFormValues,
    { resetForm }: FormikHelpers<DuplicateRoomsFormValues>,
  ) {
    try {
      await onSubmit(values.pieces.map((id) => parseInt(id, 10)));
      resetForm();
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  return (
    <Formik<DuplicateRoomsFormValues>
      initialValues={{ pieces: [] }}
      validationSchema={object({
        pieces: array()
          .of(number().integer())
          .min(1, "Veuillez sélectionner au moins une pièce"),
      })}
      onSubmit={submitHandler}
    >
      {({ errors, resetForm }) => (
        <ModalDialog
          size="lg"
          open={open}
          header="Dupliquer une pièce d'un autre logement"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setFilter("");
            }
            onOpenChange(nextOpen);
          }}
        >
          <Form className={styles.form}>
            <Text>
              Sélectionnez les pièces que vous souhaitez importer dans ce bien.
            </Text>
            <SearchInput
              placeholder="Filtrer par bien"
              onChange={(e) => setFilter(e.target.value.toLowerCase())}
            />
            <Callout variant="danger">{errors.pieces}</Callout>
            <div className={styles.properties}>
              {properties
                .filter(
                  (property) =>
                    property.id !== propertyId &&
                    property.nom.toLowerCase().includes(filter) &&
                    property.edl_items?.length,
                )
                .map((property) => (
                  <div key={property.id} className={styles.propertyGroup}>
                    <Text weight="bold" className={styles.propertyName}>
                      {property.nom}
                    </Text>
                    <div className={styles.pieces}>
                      {property.edl_items?.map((edlItem) => (
                        <Checkbox
                          key={edlItem.id}
                          name="pieces"
                          pillStyle
                          value={edlItem.id.toString()}
                        >
                          {edlItem.nom}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
            <ModalDialogFooter>
              <FormikSubmitButton requiresWrite>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
